import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import '../style/Adminorders.css';


const extractInvoiceName = (url) => {
    if (!url) return "N/A";
    const urlParts = url.split('invoices%2F');
    const fileNameWithExtension = urlParts[1]?.split('?')[0];
    return decodeURIComponent(fileNameWithExtension || '');
};

const SalesInvoice = () => {
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState('All');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [exporting, setExporting] = useState(false);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const allInvoices = [];
            const brandSet = new Set();

            for (const userDoc of usersSnapshot.docs) {
                const userData = userDoc.data();
                const brandName = userData.brandName || 'Unknown Brand';
                const userId = userDoc.id;
                const gstNumber = userData.gstNumber || '';
                
                // Fetch from orders collection as before
                const ordersCollection = collection(db, 'users', userId, 'orders');
                const ordersQuery = query(ordersCollection, where('invoiceUrl', '!=', null));
                const ordersSnapshot = await getDocs(ordersQuery);

                for (const orderDoc of ordersSnapshot.docs) {
                    const orderData = orderDoc.data();
                    const orderId = orderDoc.id;
                    
                    // Try to get createdAt from confirmed_orders collection
                    let orderDate = 'N/A';
                    try {
                        const confirmedOrderRef = doc(db, 'users', userId, 'confirmed_orders', orderId);
                        const confirmedOrderSnap = await getDoc(confirmedOrderRef);
                        
                        if (confirmedOrderSnap.exists()) {
                            const confirmedOrderData = confirmedOrderSnap.data();
                            if (confirmedOrderData.createdAt) {
                                orderDate = new Date(confirmedOrderData.createdAt.toDate ? confirmedOrderData.createdAt.toDate() : confirmedOrderData.createdAt).toISOString().split('T')[0];
                            }
                        }
                    } catch (error) {
                        console.error(`Error fetching confirmed order for ${orderId}:`, error);
                    }
                    
                    // Fallback to original date logic if createdAt not found in confirmed_orders
                    if (orderDate === 'N/A') {
                        if (orderData.updatedAt) {
                            orderDate = new Date(orderData.updatedAt.toDate ? orderData.updatedAt.toDate() : orderData.updatedAt).toISOString().split('T')[0];
                        } else if (orderData.order_date) {
                            orderDate = orderData.order_date.split('T')[0];
                        }
                    }
                    
                    const invoiceUrl = orderData.invoiceUrl;
                    const invoiceName = extractInvoiceName(invoiceUrl);
                    const UniteeTotalPrice = orderData.UniteeTotalPrice;

                    allInvoices.push({
                        id: orderDoc.id,
                        userId: userId,
                        brandName: brandName,
                        gstNumber: gstNumber,
                        invoiceUrl: invoiceUrl,
                        invoiceName: invoiceName,
                        orderDate: orderDate,
                        UniteeTotalPrice: UniteeTotalPrice,
                        order_items: orderData.order_items || [],
                        billing_customer_name: orderData.billing_customer_name,
                        billing_email: orderData.billing_email,
                        billing_phone: orderData.billing_phone,
                        billing_address: orderData.billing_address,
                        billing_city: orderData.billing_city,
                        billing_state: orderData.billing_state,
                        billing_pincode: orderData.billing_pincode,
                        billing_country: orderData.billing_country,
                        Storeorder_id: orderData.Storeorder_id,
                        payment_method: orderData.payment_method,
                        status: orderData.status,
                        UniteeShipping: orderData.UniteeShipping,
                        tracking_id: orderData.tracking_id,
                        courier_name: orderData.courier_name,
                        // Store the complete order data and user data
                        completeOrderData: orderData,
                        userData: userData
                    });

                    brandSet.add(brandName);
                    brandSet.add(gstNumber);
                }
            }

            setInvoices(allInvoices);
            setFilteredInvoices(allInvoices);
            setBrands(['All', ...Array.from(brandSet)]);
        } catch (error) {
            console.error("Error fetching invoices:", error);
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch design details by SKU
    const fetchDesignDetailsBySKU = async (userId, sku) => {
        if (!sku) {
            console.error("No SKU provided");
            return null;
        }
    
        try {
            const designsCollection = collection(db, 'users', userId, 'designs');
            
            // Try to find designs with SKU in the top-level SKUs array
            const q = query(designsCollection, where('SKUs', 'array-contains', sku));
            const designSnapshot = await getDocs(q);
    
            if (!designSnapshot.empty) {
                const designData = designSnapshot.docs[0].data();
                
                let pickupLocation = 'Printing Unit'; // Default location
                if (designData.printMethod === 'DTG Epson' || designData.printMethod === 'DTF Epson') {
                    pickupLocation = 'Printing Unit 2';
                } else if (designData.printMethod === 'DTG Brother') {
                    pickupLocation = 'Printing Unit';
                }
                
                return {
                    ...designData,
                    frontImage: designData.images?.front,
                    backImage: designData.images?.back,
                    pickupLocation,
                    designId: designSnapshot.docs[0].id
                };
            } else {
                // If not found in top-level SKUs, look in colorVariants
                const allDesignsQuery = query(designsCollection);
                const allDesignsSnapshot = await getDocs(allDesignsQuery);
                
                let matchedDesign = null;
                let matchedVariant = null;
                let designId = null;
                
                for (const doc of allDesignsSnapshot.docs) {
                    const designData = doc.data();
                    
                    if (designData.colorVariants && Array.isArray(designData.colorVariants)) {
                        for (const variant of designData.colorVariants) {
                            if (variant.SKUs && Array.isArray(variant.SKUs) && variant.SKUs.includes(sku)) {
                                matchedDesign = designData;
                                matchedVariant = variant;
                                designId = doc.id;
                                break;
                            }
                        }
                    }
                    
                    if (matchedDesign) break;
                }
                
                if (matchedDesign) {
                    let pickupLocation = 'Printing Unit'; // Default location
                    if (matchedDesign.printMethod === 'DTG Epson' || matchedDesign.printMethod === 'DTF Epson') {
                        pickupLocation = 'Printing Unit 2';
                    } else if (matchedDesign.printMethod === 'DTG Brother') {
                        pickupLocation = 'Printing Unit';
                    }
                    
                    return {
                        ...matchedDesign,
                        ...matchedVariant,
                        frontImage: matchedVariant.images?.front,
                        backImage: matchedVariant.images?.back,
                        pickupLocation,
                        designId: designId,
                        variantData: matchedVariant
                    };
                }
                
                return null;
            }
        } catch (error) {
            console.error("Error fetching design details by SKU:", error);
            return null;
        }
    };

    const handleExportToExcel = async () => {
        setExporting(true);
        
        try {
            const exportData = [];
            
            // Process each invoice/order
            for (const invoice of filteredInvoices) {
                const order = invoice.completeOrderData;
                const userData = invoice.userData;
                
                // Check if order has items
                if (!order.order_items || order.order_items.length === 0) {
                    // Calculate shipping taxes based on user state
                    const isUserMaharashtra = (userData.state === 'Maharashtra');
                    let shippingCGST = 0, shippingSGST = 0, shippingIGST = 0;
                    let shippingWithoutTax = 0;
                    
                    if (order.UniteeShipping) {
                        shippingWithoutTax = order.UniteeShipping / 1.18;
                        const shippingTax = order.UniteeShipping - shippingWithoutTax;
                        
                        if (isUserMaharashtra) {
                            shippingCGST = shippingTax / 2; // 9%
                            shippingSGST = shippingTax / 2; // 9%
                        } else {
                            shippingIGST = shippingTax; // 18%
                        }
                    }
                    
                    // Add a single row with NA for item fields
                    exportData.push({
                        'Brand Name': userData.brandName || 'NA',
                        'GST Number': userData.gstNumber || 'NA',
                        'Store Order ID': order.Storeorder_id || 'NA',
                        'Unitee Order ID': invoice.id,
                        'Invoice Name': invoice.invoiceName,
                        'SKU': 'NA',
                        'Product Name': 'NA',
                        'Variant': 'NA',
                        'Print Method': 'NA',
                        'Pickup Location': 'NA',
                        'Quantity': 'NA',
                        'Item Price': 'NA',
                        'SGST': 'NA',
                        'CGST': 'NA',
                        'IGST': 'NA',
                        'Item Total With Tax': 'NA',
                        'RTOInventory': 'NA',
                        'RTO Charges': 'NA',
                        'SubTotal': 'NA',
                        'Shipping (without tax)': shippingWithoutTax.toFixed(2),
                        'Shipping CGST (9%)': shippingCGST.toFixed(2),
                        'Shipping SGST (9%)': shippingSGST.toFixed(2),
                        'Shipping IGST (18%)': shippingIGST.toFixed(2),
                        'Shipping Total': order.UniteeShipping || 'NA',
                        'Total Price': invoice.UniteeTotalPrice || 'NA',
                        'Ship to Customer Name': userData.brandName || 'NA',
                        'Email': userData.emailAddress || 'NA',
                        'Phone': userData.phoneNumber || 'NA',
                        'Ship To Address': userData.address || 'NA',
                        'City': userData.city || 'NA',
                        'State': userData.state || 'NA',
                        'PinCode': userData.pincode || 'NA',
                        'Payment Method': order.payment_method || 'NA',
                        'Order Date': invoice.orderDate || 'NA',
                        'Order Status': order.status || 'NA',
                        'Tracking ID': order.tracking_id || 'NA',
                        'Courier Name': order.courier_name || 'NA',
                    });
                } else {
                    // Calculate total item price with tax for the order
                    
                    let isFirstItem = true;
                    let orderItemsTotal = 0;
                    let totalRTOCharges = 0;
                    
                    // Determine if user is from Maharashtra for tax calculation
                    const isUserMaharashtra = (userData.state === 'Maharashtra');
                    
                    // Calculate shipping taxes based on user state
                    let shippingCGST = 0, shippingSGST = 0, shippingIGST = 0;
                    let shippingWithoutTax = 0;
                    
                    if (order.UniteeShipping) {
                        shippingWithoutTax = order.UniteeShipping / 1.18;
                        const shippingTax = order.UniteeShipping - shippingWithoutTax;
                        
                        if (isUserMaharashtra) {
                            shippingCGST = shippingTax / 2; // 9%
                            shippingSGST = shippingTax / 2; // 9%
                        } else {
                            shippingIGST = shippingTax; // 18%
                        }
                    }
                    
                    // Process each item in the order
                    for (const item of order.order_items) {
                        // Fetch design details to get product info and pricing
                        const designData = await fetchDesignDetailsBySKU(invoice.userId, item.sku);
                        
                        // Check if item is RTO inventory
                        const isRTOinventory = item.RTOinventory === true;
                        const rtoCharges = isRTOinventory ? 50 : 0;
                        
                        // Set item price to 0 if it's RTO inventory
                        const itemPrice = isRTOinventory ? 0 : (designData?.totalPrice || 0);
                        const itemQuantity = item.units || 1;
                        
                        // Calculate tax based on price and USER STATE (not billing state)
                        let sgst = 0, cgst = 0, igst = 0;
                        let totalTax = 0;
                        
                        if (isUserMaharashtra) {
                            // Apply SGST and CGST
                            if (itemPrice < 1000) {
                                sgst = itemPrice * 0.025;
                                cgst = itemPrice * 0.025;
                            } else {
                                sgst = itemPrice * 0.06;
                                cgst = itemPrice * 0.06;
                            }
                            totalTax = sgst + cgst;
                        } else {
                            // Apply IGST
                            if (itemPrice < 1000) {
                                igst = itemPrice * 0.05;
                            } else {
                                igst = itemPrice * 0.12;
                            }
                            totalTax = igst;
                        }
                        
                        // Calculate item total with tax
                        const itemTotalWithTax = (itemPrice + totalTax) * itemQuantity;
                        totalRTOCharges += isRTOinventory ? rtoCharges : 0;
                        orderItemsTotal += itemTotalWithTax + (isRTOinventory ? rtoCharges : 0);
                        
                        exportData.push({
                            'Order Date': invoice.orderDate || 'NA',
                            'Invoice Name': invoice.invoiceName,
                            'Brand Name': userData.brandName || 'NA',
                            'GST Number': userData.gstNumber || 'NA',
                            'Unitee Order ID': invoice.id,

                            'Ship to Customer Name': userData.brandName || 'NA',
                            'Email': userData.emailAddress || 'NA',
                            'Phone': userData.phoneNumber || 'NA',
                            'Ship To Address': userData.address || 'NA',
                            'City': userData.city || 'NA',
                            'State': userData.state || 'NA',
                            'PinCode': userData.pincode || 'NA',
                            'SKU': item.sku || 'NA',
                            'Product Name': designData?.designName || item.productName || 'NA',
                            'Variant': item.name || 'NA',
                            'Quantity': itemQuantity,
                            'Item Price': itemPrice.toFixed(2),
                            'SGST': sgst.toFixed(2),
                            'CGST': cgst.toFixed(2),
                            'IGST': igst.toFixed(2),
                            'Item Total With Tax': itemTotalWithTax.toFixed(2),
                            'RTOInventory': isRTOinventory ? 'Yes' : 'No',
                            'RTO Charges': rtoCharges.toFixed(2),
                            'SubTotal': orderItemsTotal.toFixed(2),
                            'Shipping (without tax)': shippingWithoutTax.toFixed(2),
                            'Shipping CGST (9%)': shippingCGST.toFixed(2),
                            'Shipping SGST (9%)': shippingSGST.toFixed(2),
                            'Shipping IGST (18%)': shippingIGST.toFixed(2),
                            'Shipping Total': order.UniteeShipping || 'NA',
                            'Total Price': isFirstItem ? (invoice.UniteeTotalPrice || orderItemsTotal.toFixed(2)) : '',
                            'Payment Method': order.payment_method || 'NA'
                        });

                        isFirstItem = false;
                    }
                }
            }
            
            // Create worksheet
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Invoices");
            
            // Apply column widths for better readability
            const columnWidths = [
                { wch: 12 }, // Order Date
                { wch: 20 }, // Invoice Name
                { wch: 15 }, // Brand Name
                { wch: 15 }, // GST Number
                { wch: 15 }, // Unitee Order ID
                { wch: 20 }, // Ship to Customer Name
                { wch: 25 }, // Email
                { wch: 15 }, // Phone
                { wch: 30 }, // Ship To Address
                { wch: 15 }, // City
                { wch: 15 }, // State
                { wch: 10 }, // PinCode
                { wch: 15 }, // SKU
                { wch: 25 }, // Product Name
                { wch: 15 }, // Variant
                { wch: 10 }, // Quantity
                { wch: 12 }, // Item Price
                { wch: 10 }, // SGST
                { wch: 10 }, // CGST
                { wch: 10 }, // IGST
                { wch: 15 }, // Item Total With Tax
                { wch: 12 }, // RTOInventory
                { wch: 12 }, // RTO Charges
                { wch: 12 }, // SubTotal
                { wch: 18 }, // Shipping (without tax)
                { wch: 15 }, // Shipping CGST (9%)
                { wch: 15 }, // Shipping SGST (9%)
                { wch: 15 }, // Shipping IGST (18%)
                { wch: 15 }, // Shipping Total
                { wch: 12 }, // Total Price
                { wch: 15 }, // Payment Method
            ];
            
            worksheet['!cols'] = columnWidths;
            
            // Write to file
            XLSX.writeFile(workbook, `Sales_Invoices_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
        } catch (error) {
            console.error("Error generating Excel file:", error);
            alert("Failed to generate Excel file. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    useEffect(() => {
        let filtered = invoices;

        if (selectedBrand !== 'All') {
            filtered = filtered.filter(inv => inv.brandName === selectedBrand);
        }

        if (fromDate) {
            filtered = filtered.filter(inv => inv.orderDate >= fromDate);
        }

        if (toDate) {
            filtered = filtered.filter(inv => inv.orderDate <= toDate);
        }

        setFilteredInvoices(filtered);
    }, [selectedBrand, fromDate, toDate, invoices]);

    const totalPrice = filteredInvoices.reduce((sum, inv) => {
        const price = parseFloat(inv.UniteeTotalPrice) || 0;
        return sum + price;
    }, 0);

    const handleDownloadInvoice = (invoiceUrl) => {
        if (invoiceUrl) {
            window.open(invoiceUrl, '_blank');
        } else {
            alert('Invoice not available for this order.');
        }
    };

    if (loading) return <div>Loading invoices...</div>;

    return (
        <div className="admin-orders-page">
            <div className="orders-container">
                <div className="main-content">
                    <h1>Sales Invoices</h1>

                    {/* Brand Filter Dropdown */}
                    <div className="brand-filter">
                        <label htmlFor="brand-select">Filter by Brand: </label>
                        <select
                            id="brand-select"
                            value={selectedBrand}
                            onChange={(e) => setSelectedBrand(e.target.value)}
                        >
                            {brands.map((brand, index) => (
                                <option key={index} value={brand}>
                                    {brand}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Date Filter */}
                    <div className="date-filter" style={{ marginTop: '1rem' }}>
                        <label htmlFor="from-date">From Date: </label>
                        <input
                            type="date"
                            id="from-date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                        <label htmlFor="to-date" style={{ marginLeft: '1rem' }}>To Date: </label>
                        <input
                            type="date"
                            id="to-date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                        <button 
                            onClick={handleExportToExcel} 
                            className="export-btn"
                            disabled={exporting}
                        >
                            {exporting ? 'Exporting...' : 'Export to Excel'}
                        </button>
                    </div>

                    {/* Invoice Count */}
                    <div className="invoice-count" style={{ marginTop: '1rem' }}>
                        <strong>Total Invoices:</strong> {filteredInvoices.length}
                    </div>

                    {/* Total Price Display */}
                    <div className="invoice-total-price" style={{ margin: '1rem 0', fontWeight: 'bold' }}>
                        Total Price: ₹{totalPrice.toFixed(2)}
                    </div>

                    <table className="admin-orders-table">
                        <thead>
                            <tr>
                                <th>Brand Name</th>
                                <th>Order Number</th>
                                <th>Invoice Name</th>
                                <th>Unitee Total Price</th>
                                <th>Order Date</th>
                                <th>Invoice</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInvoices.map((invoice) => (
                                <tr key={invoice.id}>
                                    <td>{invoice.brandName}</td>
                                    <td>{invoice.id}</td>
                                    <td>{invoice.invoiceName}</td>
                                    <td>₹{parseFloat(invoice.UniteeTotalPrice || 0).toFixed(2)}</td>
                                    <td>{invoice.orderDate}</td>
                                    <td>
                                        <button
                                            onClick={() => handleDownloadInvoice(invoice.invoiceUrl)}
                                            className="download-invoice-btn"
                                        >
                                            Download Invoice
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filteredInvoices.length === 0 && (
                        <p style={{ marginTop: '1rem' }}>No invoices found for this brand.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesInvoice;

// import React, { useEffect, useState } from 'react';
// import { db } from '../firebase';
// import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
// import * as XLSX from 'xlsx';
// import '../style/Adminorders.css';


// const extractInvoiceName = (url) => {
//     if (!url) return "N/A";
//     const urlParts = url.split('invoices%2F');
//     const fileNameWithExtension = urlParts[1]?.split('?')[0];
//     return decodeURIComponent(fileNameWithExtension || '');
// };

// const SalesInvoice = () => {
//     const [invoices, setInvoices] = useState([]);
//     const [filteredInvoices, setFilteredInvoices] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [brands, setBrands] = useState([]);
//     const [selectedBrand, setSelectedBrand] = useState('All');
//     const [fromDate, setFromDate] = useState('');
//     const [toDate, setToDate] = useState('');
//     const [exporting, setExporting] = useState(false);

//     const fetchInvoices = async () => {
//         setLoading(true);
//         try {
//             const usersSnapshot = await getDocs(collection(db, 'users'));
//             const allInvoices = [];
//             const brandSet = new Set();

//             for (const userDoc of usersSnapshot.docs) {
//                 const userData = userDoc.data();
//                 const brandName = userData.brandName || 'Unknown Brand';
//                 const userId = userDoc.id;
//                 const gstNumber = userData.gstNumber || '';
                
//                 // Fetch from orders collection as before
//                 const ordersCollection = collection(db, 'users', userId, 'orders');
//                 const ordersQuery = query(ordersCollection, where('invoiceUrl', '!=', null));
//                 const ordersSnapshot = await getDocs(ordersQuery);

//                 for (const orderDoc of ordersSnapshot.docs) {
//                     const orderData = orderDoc.data();
//                     const orderId = orderDoc.id;
                    
//                     // Try to get createdAt from confirmed_orders collection
//                     let orderDate = 'N/A';
//                     try {
//                         const confirmedOrderRef = doc(db, 'users', userId, 'confirmed_orders', orderId);
//                         const confirmedOrderSnap = await getDoc(confirmedOrderRef);
                        
//                         if (confirmedOrderSnap.exists()) {
//                             const confirmedOrderData = confirmedOrderSnap.data();
//                             if (confirmedOrderData.createdAt) {
//                                 orderDate = new Date(confirmedOrderData.createdAt.toDate ? confirmedOrderData.createdAt.toDate() : confirmedOrderData.createdAt).toISOString().split('T')[0];
//                             }
//                         }
//                     } catch (error) {
//                         console.error(`Error fetching confirmed order for ${orderId}:`, error);
//                     }
                    
//                     // Fallback to original date logic if createdAt not found in confirmed_orders
//                     if (orderDate === 'N/A') {
//                         if (orderData.updatedAt) {
//                             orderDate = new Date(orderData.updatedAt.toDate ? orderData.updatedAt.toDate() : orderData.updatedAt).toISOString().split('T')[0];
//                         } else if (orderData.order_date) {
//                             orderDate = orderData.order_date.split('T')[0];
//                         }
//                     }
                    
//                     const invoiceUrl = orderData.invoiceUrl;
//                     const invoiceName = extractInvoiceName(invoiceUrl);
//                     const UniteeTotalPrice = orderData.UniteeTotalPrice;

//                     allInvoices.push({
//                         id: orderDoc.id,
//                         userId: userId,
//                         brandName: brandName,
//                         gstNumber: gstNumber,
//                         invoiceUrl: invoiceUrl,
//                         invoiceName: invoiceName,
//                         orderDate: orderDate,
//                         UniteeTotalPrice: UniteeTotalPrice,
//                         order_items: orderData.order_items || [],
//                         billing_customer_name: orderData.billing_customer_name,
//                         billing_email: orderData.billing_email,
//                         billing_phone: orderData.billing_phone,
//                         billing_address: orderData.billing_address,
//                         billing_city: orderData.billing_city,
//                         billing_state: orderData.billing_state,
//                         billing_pincode: orderData.billing_pincode,
//                         billing_country: orderData.billing_country,
//                         Storeorder_id: orderData.Storeorder_id,
//                         payment_method: orderData.payment_method,
//                         status: orderData.status,
//                         UniteeShipping: orderData.UniteeShipping,
//                         tracking_id: orderData.tracking_id,
//                         courier_name: orderData.courier_name,
//                         // Store the complete order data
//                         completeOrderData: orderData
//                     });

//                     brandSet.add(brandName);
//                     brandSet.add(gstNumber);
//                 }
//             }

//             setInvoices(allInvoices);
//             setFilteredInvoices(allInvoices);
//             setBrands(['All', ...Array.from(brandSet)]);
//         } catch (error) {
//             console.error("Error fetching invoices:", error);
//         } finally {
//             setLoading(false);
//         }
//     };

//     // Function to fetch design details by SKU
//     const fetchDesignDetailsBySKU = async (userId, sku) => {
//         if (!sku) {
//             console.error("No SKU provided");
//             return null;
//         }
    
//         try {
//             const designsCollection = collection(db, 'users', userId, 'designs');
            
//             // Try to find designs with SKU in the top-level SKUs array
//             const q = query(designsCollection, where('SKUs', 'array-contains', sku));
//             const designSnapshot = await getDocs(q);
    
//             if (!designSnapshot.empty) {
//                 const designData = designSnapshot.docs[0].data();
                
//                 let pickupLocation = 'Printing Unit'; // Default location
//                 if (designData.printMethod === 'DTG Epson' || designData.printMethod === 'DTF Epson') {
//                     pickupLocation = 'Printing Unit 2';
//                 } else if (designData.printMethod === 'DTG Brother') {
//                     pickupLocation = 'Printing Unit';
//                 }
                
//                 return {
//                     ...designData,
//                     frontImage: designData.images?.front,
//                     backImage: designData.images?.back,
//                     pickupLocation,
//                     designId: designSnapshot.docs[0].id
//                 };
//             } else {
//                 // If not found in top-level SKUs, look in colorVariants
//                 const allDesignsQuery = query(designsCollection);
//                 const allDesignsSnapshot = await getDocs(allDesignsQuery);
                
//                 let matchedDesign = null;
//                 let matchedVariant = null;
//                 let designId = null;
                
//                 for (const doc of allDesignsSnapshot.docs) {
//                     const designData = doc.data();
                    
//                     if (designData.colorVariants && Array.isArray(designData.colorVariants)) {
//                         for (const variant of designData.colorVariants) {
//                             if (variant.SKUs && Array.isArray(variant.SKUs) && variant.SKUs.includes(sku)) {
//                                 matchedDesign = designData;
//                                 matchedVariant = variant;
//                                 designId = doc.id;
//                                 break;
//                             }
//                         }
//                     }
                    
//                     if (matchedDesign) break;
//                 }
                
//                 if (matchedDesign) {
//                     let pickupLocation = 'Printing Unit'; // Default location
//                     if (matchedDesign.printMethod === 'DTG Epson' || matchedDesign.printMethod === 'DTF Epson') {
//                         pickupLocation = 'Printing Unit 2';
//                     } else if (matchedDesign.printMethod === 'DTG Brother') {
//                         pickupLocation = 'Printing Unit';
//                     }
                    
//                     return {
//                         ...matchedDesign,
//                         ...matchedVariant,
//                         frontImage: matchedVariant.images?.front,
//                         backImage: matchedVariant.images?.back,
//                         pickupLocation,
//                         designId: designId,
//                         variantData: matchedVariant
//                     };
//                 }
                
//                 return null;
//             }
//         } catch (error) {
//             console.error("Error fetching design details by SKU:", error);
//             return null;
//         }
//     };

//     const handleExportToExcel = async () => {
//         setExporting(true);
        
//         try {
//             const exportData = [];
            
//             // Process each invoice/order
//             for (const invoice of filteredInvoices) {
//                 const order = invoice.completeOrderData;
                
//                 // Check if order has items
//                 if (!order.order_items || order.order_items.length === 0) {
//                     // Add a single row with NA for item fields
//                     exportData.push({
//                         'Brand Name': invoice.brandName,
//                         'GST Number': invoice.gstNumber,
//                         'Store Order ID': order.Storeorder_id || 'NA',
//                         'Unitee Order ID': invoice.id,
//                         'Invoice Name': invoice.invoiceName,
//                         'SKU': 'NA',
//                         'Product Name': 'NA',
//                         'Variant': 'NA',
//                         'Print Method': 'NA',
//                         'Pickup Location': 'NA',
//                         'Quantity': 'NA',
//                         'Item Price': 'NA',
//                         'SGST': 'NA',
//                         'CGST': 'NA',
//                         'IGST': 'NA',
//                         'Item Total With Tax': 'NA',
//                         'RTOInventory': 'NA',
//                         'RTO Charges': 'NA',
//                         'SubTotal': 'NA',
//                         'Shipping (without tax)': (order.UniteeShipping ? (order.UniteeShipping / 1.18).toFixed(2) : 'NA'),
//                         'Shipping Tax (18%)': (order.UniteeShipping ? (order.UniteeShipping - (order.UniteeShipping / 1.18)).toFixed(2) : 'NA'),
//                         'Shipping Total': order.UniteeShipping || 'NA',
//                         'Total Price': invoice.UniteeTotalPrice || 'NA',
//                         'Customer Name': order.billing_customer_name || 'NA',
//                         'Email': order.billing_email || 'NA',
//                         'Phone': order.billing_phone || 'NA',
//                         'Billing Address': order.billing_address || 'NA',
//                         'City': order.billing_city || 'NA',
//                         'State': order.billing_state || 'NA',
//                         'Pincode': order.billing_pincode || 'NA',
//                         'Country': order.billing_country || 'NA',
//                         'Payment Method': order.payment_method || 'NA',
//                         'Order Date': invoice.orderDate || 'NA',
//                         'Order Status': order.status || 'NA',
//                         'Tracking ID': order.tracking_id || 'NA',
//                         'Courier Name': order.courier_name || 'NA',
//                     });
//                 } else {
//                     // Calculate total item price with tax for the order
                    
//                     let isFirstItem = true;
//                     let orderItemsTotal = 0;
//                     let totalRTOCharges = 0;
                    
//                     // Process each item in the order
//                     for (const item of order.order_items) {
//                         // Fetch design details to get product info and pricing
//                         const designData = await fetchDesignDetailsBySKU(invoice.userId, item.sku);
                        
//                         // Check if item is RTO inventory
//                         const isRTOinventory = item.RTOinventory === true;
//                         const rtoCharges = isRTOinventory ? 50 : 0;
                        
//                         // Set item price to 0 if it's RTO inventory
//                         const itemPrice = isRTOinventory ? 0 : (designData?.totalPrice || 0);
//                         const itemQuantity = item.units || 1;
                        
//                         // Calculate tax based on price and state
//                         const isMaharashtra = (order.billing_state === 'Maharashtra');
//                         let sgst = 0, cgst = 0, igst = 0;
//                         let totalTax = 0;
                        
//                         if (isMaharashtra) {
//                             // Apply SGST and CGST
//                             if (itemPrice < 1000) {
//                                 sgst = itemPrice * 0.025;
//                                 cgst = itemPrice * 0.025;
//                             } else {
//                                 sgst = itemPrice * 0.06;
//                                 cgst = itemPrice * 0.06;
//                             }
//                             totalTax = sgst + cgst;
//                         } else {
//                             // Apply IGST
//                             if (itemPrice < 1000) {
//                                 igst = itemPrice * 0.05;
//                             } else {
//                                 igst = itemPrice * 0.12;
//                             }
//                             totalTax = igst;
//                         }
                        
//                         // Calculate item total with tax
//                         const itemTotalWithTax = (itemPrice + totalTax) * itemQuantity;
//                         totalRTOCharges += isRTOinventory ? rtoCharges : 0;
//                         orderItemsTotal += itemTotalWithTax + (isRTOinventory ? rtoCharges : 0);
                        
//                         exportData.push({
//                             'Order Date': invoice.orderDate || 'NA',
//                             'Invoice Name': invoice.invoiceName,
//                             'Brand Name': invoice.brandName,
//                             'GST Number': invoice.gstNumber,
//                             'Unitee Order ID': invoice.id,

//                             'Ship to Customer Name': order.billing_customer_name || 'NA',
//                             'Email': order.billing_email || 'NA',
//                             'Phone': order.billing_phone || 'NA',
//                             'Ship To Address': order.billing_address || 'NA',
//                             'City': order.billing_city || 'NA',
//                             'State': order.billing_state || 'NA',
//                             'PinCode': order.billing_pincode || 'NA',
//                             'SKU': item.sku || 'NA',
//                             'Product Name': designData?.designName || item.productName || 'NA',
//                             'Variant': item.name || 'NA',
//                             'Quantity': itemQuantity,
//                             'Item Price': itemPrice.toFixed(2),
//                             'SGST': sgst.toFixed(2),
//                             'CGST': cgst.toFixed(2),
//                             'IGST': igst.toFixed(2),
//                             'Item Total With Tax': itemTotalWithTax.toFixed(2),
//                             'RTOInventory': isRTOinventory ? 'Yes' : 'No',
//                             'RTO Charges': rtoCharges.toFixed(2),
//                             'SubTotal': orderItemsTotal.toFixed(2),
//                             'Shipping (without tax)': (order.UniteeShipping ? (order.UniteeShipping / 1.18).toFixed(2) : 'NA'),
//                             'Shipping Tax (18%)': (order.UniteeShipping ? (order.UniteeShipping - (order.UniteeShipping / 1.18)).toFixed(2) : 'NA'),
//                             'Shipping Total': order.UniteeShipping || 'NA',
//                             'Total Price': isFirstItem ? (invoice.UniteeTotalPrice || orderItemsTotal.toFixed(2)) : '',
//                             'Payment Method': order.payment_method || 'NA'
//                         });

//                         isFirstItem = false;
//                     }
//                 }
//             }
            
//             // Create worksheet
//             const worksheet = XLSX.utils.json_to_sheet(exportData);
//             const workbook = XLSX.utils.book_new();
//             XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Invoices");
            
//             // Apply column widths for better readability
//             const columnWidths = [
//                 { wch: 15 }, // Brand Name
//                 { wch: 15 }, // Store Order ID
//                 { wch: 15 }, // Unitee Order ID
//                 { wch: 20 }, // Invoice Name
//                 { wch: 15 }, // SKU
//                 { wch: 25 }, // Product Name
//                 { wch: 15 }, // Variant
//                 { wch: 15 }, // Print Method
//                 { wch: 15 }, // Pickup Location
//                 { wch: 10 }, // Quantity
//                 { wch: 12 }, // Item Price
//                 { wch: 10 }, // SGST
//                 { wch: 10 }, // CGST
//                 { wch: 10 }, // IGST
//                 { wch: 15 }, // Item Total With Tax
//                 { wch: 12 }, // RTOInventory
//                 { wch: 12 }, // RTO Charges
//                 { wch: 12 }, // SubTotal
//                 { wch: 18 }, // Shipping (without tax)
//                 { wch: 18 }, // Shipping Tax (18%)
//                 { wch: 15 }, // Shipping Total
//                 { wch: 12 }, // Total Price
//                 { wch: 20 }, // Customer Name
//                 { wch: 25 }, // Email
//                 { wch: 15 }, // Phone
//                 { wch: 30 }, // Billing Address
//                 { wch: 15 }, // City
//                 { wch: 15 }, // State
//                 { wch: 10 }, // Pincode
//                 { wch: 10 }, // Country
//                 { wch: 15 }, // Payment Method
//                 { wch: 12 }, // Order Date
//                 { wch: 15 }, // Order Status
//                 { wch: 20 }, // Tracking ID
//                 { wch: 15 }, // Courier Name
//             ];
            
//             worksheet['!cols'] = columnWidths;
            
//             // Write to file
//             XLSX.writeFile(workbook, `Sales_Invoices_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
//         } catch (error) {
//             console.error("Error generating Excel file:", error);
//             alert("Failed to generate Excel file. Please try again.");
//         } finally {
//             setExporting(false);
//         }
//     };

//     useEffect(() => {
//         fetchInvoices();
//     }, []);

//     useEffect(() => {
//         let filtered = invoices;

//         if (selectedBrand !== 'All') {
//             filtered = filtered.filter(inv => inv.brandName === selectedBrand);
//         }

//         if (fromDate) {
//             filtered = filtered.filter(inv => inv.orderDate >= fromDate);
//         }

//         if (toDate) {
//             filtered = filtered.filter(inv => inv.orderDate <= toDate);
//         }

//         setFilteredInvoices(filtered);
//     }, [selectedBrand, fromDate, toDate, invoices]);

//     const totalPrice = filteredInvoices.reduce((sum, inv) => {
//         const price = parseFloat(inv.UniteeTotalPrice) || 0;
//         return sum + price;
//     }, 0);

//     const handleDownloadInvoice = (invoiceUrl) => {
//         if (invoiceUrl) {
//             window.open(invoiceUrl, '_blank');
//         } else {
//             alert('Invoice not available for this order.');
//         }
//     };

//     if (loading) return <div>Loading invoices...</div>;

//     return (
//         <div className="admin-orders-page">
//             <div className="orders-container">
//                 <div className="main-content">
//                     <h1>Sales Invoices</h1>

//                     {/* Brand Filter Dropdown */}
//                     <div className="brand-filter">
//                         <label htmlFor="brand-select">Filter by Brand: </label>
//                         <select
//                             id="brand-select"
//                             value={selectedBrand}
//                             onChange={(e) => setSelectedBrand(e.target.value)}
//                         >
//                             {brands.map((brand, index) => (
//                                 <option key={index} value={brand}>
//                                     {brand}
//                                 </option>
//                             ))}
//                         </select>
//                     </div>

//                     {/* Date Filter */}
//                     <div className="date-filter" style={{ marginTop: '1rem' }}>
//                         <label htmlFor="from-date">From Date: </label>
//                         <input
//                             type="date"
//                             id="from-date"
//                             value={fromDate}
//                             onChange={(e) => setFromDate(e.target.value)}
//                         />
//                         <label htmlFor="to-date" style={{ marginLeft: '1rem' }}>To Date: </label>
//                         <input
//                             type="date"
//                             id="to-date"
//                             value={toDate}
//                             onChange={(e) => setToDate(e.target.value)}
//                         />
//                     </div>

//                     <div style={{ marginTop: '1rem' }}>
//                         <button 
//                             onClick={handleExportToExcel} 
//                             className="export-btn"
//                             disabled={exporting}
//                         >
//                             {exporting ? 'Exporting...' : 'Export to Excel'}
//                         </button>
//                     </div>

//                     {/* Invoice Count */}
//                     <div className="invoice-count" style={{ marginTop: '1rem' }}>
//                         <strong>Total Invoices:</strong> {filteredInvoices.length}
//                     </div>

//                     {/* Total Price Display */}
//                     <div className="invoice-total-price" style={{ margin: '1rem 0', fontWeight: 'bold' }}>
//                         Total Price: ₹{totalPrice.toFixed(2)}
//                     </div>

//                     <table className="admin-orders-table">
//                         <thead>
//                             <tr>
//                                 <th>Brand Name</th>
//                                 <th>Order Number</th>
//                                 <th>Invoice Name</th>
//                                 <th>Unitee Total Price</th>
//                                 <th>Order Date</th>
//                                 <th>Invoice</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {filteredInvoices.map((invoice) => (
//                                 <tr key={invoice.id}>
//                                     <td>{invoice.brandName}</td>
//                                     <td>{invoice.id}</td>
//                                     <td>{invoice.invoiceName}</td>
//                                     <td>₹{parseFloat(invoice.UniteeTotalPrice || 0).toFixed(2)}</td>
//                                     <td>{invoice.orderDate}</td>
//                                     <td>
//                                         <button
//                                             onClick={() => handleDownloadInvoice(invoice.invoiceUrl)}
//                                             className="download-invoice-btn"
//                                         >
//                                             Download Invoice
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                     </table>

//                     {filteredInvoices.length === 0 && (
//                         <p style={{ marginTop: '1rem' }}>No invoices found for this brand.</p>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default SalesInvoice;